import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Coins, Plus, Minus, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import { SeshBalance } from '@/components/sesh/SeshBalance';
import { TransactionHistory } from '@/components/sesh/TransactionHistory';
import { useWalletRepository } from '@/hooks/useRepositoryFactory';
import { supabase } from '@/integrations/supabase/client';

const SeshDebugger = () => {
  const [balance, setBalance] = useState<number | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(10);
  const [debitAmount, setDebitAmount] = useState<number>(5);
  const [description, setDescription] = useState<string>('Teste manual');
  const [isLoading, setIsLoading] = useState(false);
  const [lastOperation, setLastOperation] = useState<string>('');
  
  const { toast } = useToast();
  const walletRepository = useWalletRepository();

  const loadBalance = async () => {
    console.log('🔄 Carregando saldo atual...');
    const currentBalance = await walletRepository.getCurrentUserWalletBalance();
    setBalance(currentBalance);
    console.log('💰 Saldo atual:', currentBalance);
  };

  useEffect(() => {
    loadBalance();
  }, []);

  const handleCreditSeshs = async () => {
    setIsLoading(true);
    try {
      console.log('➕ Creditando Seshs:', creditAmount);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Usuário não autenticado');
      }

      // Usar a RPC function para adicionar Seshs
      const { data, error } = await supabase.rpc('add_seshs_to_user_simple', {
        p_user_id: session.user.id,
        p_amount: creditAmount,
        p_description: `Debug: ${description}`
      });

      if (error) {
        console.error('❌ Erro ao creditar:', error);
        throw error;
      }

      console.log('✅ Seshs creditados com sucesso');
      setLastOperation(`+${creditAmount} Seshs creditados`);
      
      toast({
        title: "Seshs Creditados",
        description: `${creditAmount} Seshs foram adicionados à sua conta.`,
      });

      // Recarregar saldo
      await loadBalance();
      
    } catch (error) {
      console.error('❌ Erro:', error);
      toast({
        title: "Erro ao Creditar",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDebitSeshs = async () => {
    setIsLoading(true);
    try {
      console.log('➖ Debitando Seshs:', debitAmount);
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        throw new Error('Usuário não autenticado');
      }

      // Verificar se tem saldo suficiente
      if (balance !== null && balance < debitAmount) {
        throw new Error('Saldo insuficiente');
      }

      // Usar a RPC function para debitar Seshs (valor negativo)
      const { data, error } = await supabase.rpc('add_seshs_to_user_simple', {
        p_user_id: session.user.id,
        p_amount: -debitAmount,
        p_description: `Debug: Débito - ${description}`
      });

      if (error) {
        console.error('❌ Erro ao debitar:', error);
        throw error;
      }

      console.log('✅ Seshs debitados com sucesso');
      setLastOperation(`-${debitAmount} Seshs debitados`);
      
      toast({
        title: "Seshs Debitados",
        description: `${debitAmount} Seshs foram removidos da sua conta.`,
      });

      // Recarregar saldo
      await loadBalance();
      
    } catch (error) {
      console.error('❌ Erro:', error);
      toast({
        title: "Erro ao Debitar",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshBalance = async () => {
    setIsLoading(true);
    try {
      console.log('🔄 Forçando atualização do saldo...');
      const refreshedBalance = await walletRepository.refreshWalletBalance();
      setBalance(refreshedBalance);
      
      toast({
        title: "Saldo Atualizado",
        description: `Saldo atual: ${refreshedBalance} Seshs`,
      });
      
    } catch (error) {
      console.error('❌ Erro ao atualizar:', error);
      toast({
        title: "Erro ao Atualizar",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const simulateAdvancedAnalysis = async () => {
    setIsLoading(true);
    try {
      console.log('🧠 Simulando análise avançada...');
      
      if (balance !== null && balance < 10) {
        throw new Error('Saldo insuficiente para análise avançada (requer 10 Seshs)');
      }

      // Simular débito de análise avançada
      await handleDebitSeshs();
      setLastOperation('Análise avançada simulada (-10 Seshs)');
      
    } catch (error) {
      console.error('❌ Erro na simulação:', error);
      toast({
        title: "Erro na Simulação",
        description: error instanceof Error ? error.message : 'Erro desconhecido',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-4xl space-y-6">
      <h1 className="text-3xl font-bold mb-6 flex items-center gap-2">
        <Coins className="h-8 w-8 text-brand-blue" />
        Debug de Seshs
      </h1>

      {/* Status Atual */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            Status Atual
            <SeshBalance variant="secondary" animated />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Saldo Local:</span>
            <Badge variant="outline" className="text-lg">
              {balance !== null ? `${balance} Seshs` : 'Carregando...'}
            </Badge>
          </div>
          
          {lastOperation && (
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Última operação: {lastOperation}</span>
            </div>
          )}

          <Button 
            onClick={handleRefreshBalance} 
            disabled={isLoading}
            variant="outline"
            className="w-full"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar Saldo
          </Button>
        </CardContent>
      </Card>

      {/* Operações de Teste */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Creditar Seshs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-600">
              <Plus className="h-5 w-5" />
              Creditar Seshs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="credit-amount">Quantidade</Label>
              <Input
                id="credit-amount"
                type="number"
                value={creditAmount}
                onChange={(e) => setCreditAmount(Number(e.target.value))}
                min="1"
                max="1000"
              />
            </div>
            
            <div>
              <Label htmlFor="credit-description">Descrição</Label>
              <Input
                id="credit-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Motivo do crédito..."
              />
            </div>

            <Button 
              onClick={handleCreditSeshs} 
              disabled={isLoading || creditAmount <= 0}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Creditar {creditAmount} Seshs
            </Button>
          </CardContent>
        </Card>

        {/* Debitar Seshs */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <Minus className="h-5 w-5" />
              Debitar Seshs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="debit-amount">Quantidade</Label>
              <Input
                id="debit-amount"
                type="number"
                value={debitAmount}
                onChange={(e) => setDebitAmount(Number(e.target.value))}
                min="1"
                max={balance || 100}
              />
            </div>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertCircle className="h-4 w-4" />
              <span>Saldo disponível: {balance || 0} Seshs</span>
            </div>

            <Button 
              onClick={handleDebitSeshs} 
              disabled={isLoading || debitAmount <= 0 || (balance !== null && balance < debitAmount)}
              variant="destructive"
              className="w-full"
            >
              <Minus className="h-4 w-4 mr-2" />
              Debitar {debitAmount} Seshs
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Simulações */}
      <Card>
        <CardHeader>
          <CardTitle>Simulações de Uso</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button 
            onClick={simulateAdvancedAnalysis} 
            disabled={isLoading || (balance !== null && balance < 10)}
            variant="outline"
            className="w-full"
          >
            🧠 Simular Análise Avançada (-10 Seshs)
          </Button>
          
          <div className="text-sm text-muted-foreground">
            <p>• Análise Avançada: Consome 10 Seshs</p>
            <p>• Sessão de Voz: Ganha 1 Sesh</p>
            <p>• Indicação Aceita: Ganha 10 Seshs</p>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Transações */}
      <TransactionHistory limit={10} />
    </div>
  );
};

export default SeshDebugger;
